import { NavLink, Outlet, useNavigate, Link } from "react-router-dom";
import {
  LayoutDashboard,
  Users,
  MessageSquare,
  Mail,
  Activity,
  LogOut,
  ExternalLink,
  Globe,
} from "lucide-react";
import { useAuth } from "../../lib/auth";
import { Toaster } from "../ui/sonner";
import useSEO from "../../lib/seo";

const links = [
  { to: "/admin", label: "Dashboard", icon: LayoutDashboard, end: true },
  { to: "/admin/leads", label: "Leads (CRM)", icon: Users },
  { to: "/admin/chats", label: "Chat Messages", icon: MessageSquare },
  { to: "/admin/contacts", label: "Contact Forms", icon: Mail },
  { to: "/admin/visitors", label: "Site Visitors", icon: Activity },
];

export default function AdminLayout() {
  useSEO({ title: "Admin", noindex: true });
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const onLogout = () => {
    logout();
    navigate("/admin/login");
  };

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-neutral-100 flex">
      {/* Sidebar */}
      <aside className="w-64 border-r border-white/10 bg-[#0d0d0d] flex flex-col flex-shrink-0">
        <div className="p-6 border-b border-white/10">
          <Link to="/" className="flex items-center gap-3">
            <span className="relative inline-flex items-center gap-1">
              <span className="relative inline-flex h-9 w-9 items-center justify-center">
                <span className="absolute inset-0 bg-[#dc2626] -skew-x-12" />
                <span className="relative font-display font-black text-white text-lg">
                  D
                </span>
              </span>
              <span className="relative inline-flex h-9 w-9 items-center justify-center rounded-full bg-[#1d4ed8] text-white">
                <Globe className="h-4 w-4" strokeWidth={2} />
              </span>
            </span>
            <div>
              <div className="font-display font-bold text-white text-base leading-tight">
                DVP Admin
              </div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-neutral-500">
                Console
              </div>
            </div>
          </Link>
        </div>

        <nav className="flex-1 p-4 space-y-1">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              end={l.end}
              data-testid={`admin-nav-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 text-sm rounded-md transition-colors ${
                  isActive
                    ? "bg-[#dc2626]/10 text-white border-l-2 border-[#dc2626] pl-2.5"
                    : "text-neutral-400 hover:text-white hover:bg-white/5"
                }`
              }
            >
              <l.icon className="h-4 w-4" />
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="p-4 border-t border-white/10 space-y-2">
          <a
            href="/"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-2 text-xs text-neutral-400 hover:text-white px-3 py-2 rounded-md hover:bg-white/5"
          >
            <ExternalLink className="h-3.5 w-3.5" /> View public site
          </a>
          <div className="px-3 py-2 text-xs text-neutral-500 truncate" title={user?.email}>
            {user?.email}
          </div>
          <button
            onClick={onLogout}
            data-testid="admin-logout-btn"
            className="w-full flex items-center gap-2 text-sm text-neutral-300 hover:text-white px-3 py-2 rounded-md hover:bg-[#dc2626]/10"
          >
            <LogOut className="h-4 w-4" /> Logout
          </button>
        </div>
      </aside>

      {/* Main */}
      <main className="flex-1 overflow-auto">
        <Outlet />
      </main>

      <Toaster theme="dark" richColors position="top-right" />
    </div>
  );
}
